import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { api, API, getToken } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { NotificationsBell } from "@/components/NotificationsBell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  LayoutDashboard, BookOpen, PlusCircle, Video, History,
  CreditCard, User, LogOut, Shield, Briefcase, Sparkles, Users, BookMarked, Bookmark, NotebookPen, GraduationCap,
  Coins, AlertTriangle,
} from "lucide-react";

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((n) => n[0]).join("").toUpperCase() || "U";
}

export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const needsCredits = !!user?.needs_credits;
  const creditBalance = Number(user?.credit_balance ?? 0);
  const LOW_BALANCE_THRESHOLD = 6;
  const lowBalance = needsCredits && creditBalance < LOW_BALANCE_THRESHOLD;

  const candidateNav = [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/app/cases", label: "Case Library", icon: BookOpen },
    { to: "/app/mock-exam", label: "Mock Exam", icon: GraduationCap },
    { to: "/app/cases/new", label: "Create Case", icon: PlusCircle },
    { to: "/app/bookmarks", label: "Bookmarked", icon: Bookmark },
    { to: "/app/connect", label: "Peer Connect", icon: Video },
    { to: "/app/friends", label: "Friends", icon: Users },
    { to: "/app/revision", label: "Revision", icon: BookMarked },
    { to: "/app/notebook", label: "Notebook", icon: NotebookPen },
    { to: "/app/history", label: "History", icon: History },
    needsCredits
      ? { to: "/app/credits", label: "Credits", icon: Coins }
      : { to: "/app/subscribe", label: "Subscription", icon: CreditCard },
  ];
  const isPartner = user?.role === "partner";
  let nav;
  if (user?.role === "partner") {
    nav = [{ to: "/app/partner", label: "Partner Dashboard", icon: Briefcase }, ...candidateNav];
  } else if (user?.role === "admin") {
    nav = [{ to: "/app/admin", label: "Admin", icon: Shield }, ...candidateNav];
  } else {
    nav = candidateNav;
  }

  const mobileNav = user?.role === "partner"
    ? [
        { to: "/app/partner", label: "Partner", icon: Briefcase },
        { to: "/app", label: "Home", icon: LayoutDashboard, end: true },
        { to: "/app/cases", label: "Cases", icon: BookOpen },
        { to: "/app/connect", label: "Connect", icon: Video },
        { to: "/app/profile", label: "Profile", icon: User },
      ]
    : [
        { to: "/app", label: "Home", icon: LayoutDashboard, end: true },
        { to: "/app/cases", label: "Cases", icon: BookOpen },
        { to: "/app/cases/new", label: "Create", icon: PlusCircle },
        { to: "/app/connect", label: "Connect", icon: Video },
        { to: "/app/profile", label: "Profile", icon: User },
      ];

  const sub = user?.subscription;
  const subActive = sub?.is_active;

  const doLogout = () => { logout(); navigate("/"); };

  // Presence heartbeat so friends can see when you're online.
  useEffect(() => {
    if (!user) return;
    let live = true;
    const ping = () => { api.presencePing().catch(() => {}); };
    ping();
    const iv = setInterval(() => { if (live) ping(); }, 20000);

    // On tab/app close, tell the server we're leaving (best-effort, survives page teardown via
    // sendBeacon). Marks us offline + not available; the server janitor then closes any rooms we
    // host. This is refresh-safe: a refresh resumes the heartbeat within a second or two.
    const goOffline = () => {
      try {
        const token = getToken();
        if (!token) return;
        const blob = new Blob([JSON.stringify({ token })], { type: "application/json" });
        if (navigator.sendBeacon) navigator.sendBeacon(`${API}/presence/offline`, blob);
      } catch (_) {}
    };
    window.addEventListener("pagehide", goOffline);
    window.addEventListener("beforeunload", goOffline);

    return () => {
      live = false;
      clearInterval(iv);
      window.removeEventListener("pagehide", goOffline);
      window.removeEventListener("beforeunload", goOffline);
    };
  }, [user]);

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside data-testid="app-sidebar" className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-border bg-card md:flex">
        <div className="flex h-16 items-center border-b border-border px-5"><Logo /></div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
            >
              <item.icon className="h-[18px] w-[18px]" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <div className="mb-2 rounded-lg bg-secondary px-3 py-2.5">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
              {needsCredits ? <><Coins className="h-3.5 w-3.5 text-primary" /> {creditBalance} credit{creditBalance === 1 ? "" : "s"}</>
                : user?.role === "admin" ? <><Shield className="h-3.5 w-3.5 text-primary" /> Administrator</>
                : user?.role === "partner" ? <><Briefcase className="h-3.5 w-3.5 text-primary" /> Course partner</>
                : <><Sparkles className="h-3.5 w-3.5 text-primary" /> {subActive ? "Premium active" : "Free account"}</>}
            </div>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {needsCredits ? "Practice 2 · Create case 1 · Mock exam 30"
                : user?.role === "admin" ? "Full platform access"
                : user?.role === "partner" ? "Track your referrals"
                : subActive ? `${sub?.plan === "quarterly" ? "3-month" : "Monthly"} plan` : "Subscribe to unlock AI practice"}
            </p>
            {needsCredits && (
              <Button size="sm" variant="outline" data-testid="sidebar-topup-button" onClick={() => navigate("/app/credits")} className="mt-2 h-7 w-full text-xs">
                Top up credits
              </Button>
            )}
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="md:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-border bg-background/80 px-4 backdrop-blur-md sm:px-6">
          <div className="md:hidden"><Logo compact /></div>
          <div className="hidden md:block" />
          <div className="flex items-center gap-3">
            {needsCredits && (
              <button
                type="button" data-testid="topbar-credit-balance" onClick={() => navigate("/app/credits")}
                className={`hidden items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors sm:inline-flex ${
                  lowBalance ? "border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/15" : "border-border bg-secondary text-foreground hover:bg-secondary/80"
                }`}>
                {lowBalance ? <AlertTriangle className="h-3.5 w-3.5" /> : <Coins className="h-3.5 w-3.5 text-primary" />}
                {creditBalance} credit{creditBalance === 1 ? "" : "s"}
              </button>
            )}
            {!subActive && !needsCredits && user?.role === "candidate" && (
              <Button size="sm" variant="outline" data-testid="topbar-upgrade" onClick={() => navigate("/app/subscribe")} className="hidden sm:inline-flex">
                Upgrade
              </Button>
            )}
            {!isPartner && <NotificationsBell />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="profile-menu" className="flex items-center gap-2 rounded-full p-0.5 outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.photo} alt={user?.name} />
                    <AvatarFallback className="bg-primary/10 text-primary">{initials(user?.name)}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="truncate font-medium text-foreground">{user?.name}</span>
                    <span className="truncate text-xs font-normal text-muted-foreground">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/app/profile")}><User className="mr-2 h-4 w-4" /> Profile</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-friends" onClick={() => navigate("/app/friends")}><Users className="mr-2 h-4 w-4" /> Friends</DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-history" onClick={() => navigate("/app/history")}><History className="mr-2 h-4 w-4" /> Practice history</DropdownMenuItem>
                {needsCredits ? (
                  <DropdownMenuItem data-testid="menu-credits" onClick={() => navigate("/app/credits")}><Coins className="mr-2 h-4 w-4" /> Credits</DropdownMenuItem>
                ) : (
                  <DropdownMenuItem data-testid="menu-subscribe" onClick={() => navigate("/app/subscribe")}><CreditCard className="mr-2 h-4 w-4" /> Subscription</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={doLogout} className="text-destructive focus:text-destructive"><LogOut className="mr-2 h-4 w-4" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main key={location.pathname} className="min-h-[calc(100vh-4rem)] animate-fade-in-up px-4 py-6 pb-24 sm:px-6 md:pb-10 lg:px-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav data-testid="mobile-bottom-tabs" className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-around border-t border-border bg-card/95 px-2 py-1.5 backdrop-blur-md md:hidden">
        {mobileNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `flex min-w-[56px] flex-col items-center gap-0.5 rounded-lg px-2 py-1.5 text-[11px] font-medium transition-colors ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`}
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
